import React, { useEffect } from 'react';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { HelmetProvider } from 'react-helmet-async';
import theme from './theme';
import { logger } from './utils/logger';
import CreateSimulation from './pages/CreateSimulation';
import CreateIdea from './pages/CreateIdea';
import SimulationResultsPage from './pages/SimulationResultsPage';
import IdeaResultsPage from './pages/IdeaResultsPage';
import LandingPage from './pages/LandingPage';
// import ComingSoonPage from './pages/ComingSoonPage';
import LoginPage from './pages/LoginPage';
import VerifyPage from './pages/VerifyPage';
import DashboardPage from './pages/DashboardPage';
import SimulationsPage from './pages/SimulationsPage';
import IdeasPage from './pages/IdeasPage';
import ICPsPage from './pages/ICPsPage';
import AccountsPage from './pages/AccountsPage';
import SettingsPage from './pages/SettingsPage';
import PlansPage from './pages/PlansPage';
import PricingPage from './pages/PricingPage';
import DocsPage from './pages/DocsPage';
// FeaturesPage removed - features section now on landing page
import SupportPage from './pages/SupportPage';
import PrivacyPolicyPage from './pages/legal/PrivacyPolicyPage';
import TermsOfServicePage from './pages/legal/TermsOfServicePage';
// Old blog pages replaced by dynamic markdown system - kept for reference
// import SocialMediaSentimentAnalysisPage from './pages/SocialMediaSentimentAnalysisPage';
// import PostSimulationToolPage from './pages/PostSimulationToolPage';
// import SocialMediaSentimentAnalysisToolsPage from './pages/SocialMediaSentimentAnalysisToolsPage';
import BlogIndexPage from './pages/BlogIndexPage';
import BlogPost from './pages/BlogPost';
import NotFoundPage from './pages/NotFoundPage';
import AuthCallbackPage from './pages/AuthCallbackPage';
// import LogoTestPage from './pages/LogoTestPage';
// import DemoPage from './pages/DemoPage';
import InternalPageLayout from './components/layout/InternalPageLayout';
import ProtectedRoute from './components/auth/ProtectedRoute';
import TermsOfServiceModal from './components/TermsOfServiceModal';
import CookieBanner from './components/CookieBanner';
import { useTermsOfService } from './hooks/useTermsOfService';
import { SimulationProvider } from './contexts/SimulationContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { initializeAnalytics } from './utils/analytics';
import { PrelaunchProvider, usePrelaunch } from './contexts/PrelaunchContext';
import { QuotaProvider } from './contexts/QuotaContext';
import { FeedbackProvider } from './contexts/FeedbackContext';
import { Analytics } from "@vercel/analytics/react"

// Redirect component for /features route
const FeaturesRedirect = () => {
  useEffect(() => {
    window.location.href = '/#features';
  }, []);
  return null;
};

// Route tracking component for debugging navigation issues
const RouteTracker = () => {
  const location = useLocation();

  useEffect(() => {
    logger.info('Route changed:', {
      pathname: location.pathname,
      search: location.search,
      state: location.state,
      timestamp: new Date().toISOString()
    });
  }, [location]);

  return null;
};

function AppContent() {
  const { showTermsModal, acceptTerms, isCheckingTerms, hasCheckedInitialTerms } = useTermsOfService();
  const { isLoading, showOnboarding } = useAuth();
  const { isPrelaunch } = usePrelaunch();
  
  useEffect(() => {
    initializeAnalytics();
  }, []);
  
  return (
    <>
      <RouteTracker />
      <Routes>
        {isPrelaunch ? (
          <Route path="/*" element={<LandingPage />} />
        ) : (
          <>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/verify" element={<VerifyPage />} />
            <Route path="/auth/callback" element={<AuthCallbackPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/features" element={<FeaturesRedirect />} />
            <Route path="/docs" element={<DocsPage />} />
            <Route path="/support" element={<SupportPage />} />
            <Route path="/legal/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/legal/terms-of-service" element={<TermsOfServicePage />} />
            {/* Redirect old blog routes to new dynamic blog routes */}
            <Route path="/social-media-sentiment-analysis" element={<Navigate to="/blog/social-media-sentiment-analysis" replace />} />
            <Route path="/post-simulation-tool" element={<Navigate to="/blog/post-simulation-tool" replace />} />
            <Route path="/social-media-sentiment-analysis-tools" element={<Navigate to="/blog/social-media-sentiment-analysis-tools" replace />} />
            <Route path="/social-sentiment-tools" element={<Navigate to="/blog/social-media-sentiment-analysis-tools" replace />} />
            <Route path="/blog" element={<BlogIndexPage />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            {/* <Route path="/demo" element={<DemoPage />} /> */}
            
            <Route element={<ProtectedRoute />}>
              <Route path="/dashboard" element={<InternalPageLayout><DashboardPage /></InternalPageLayout>} />
              <Route path="/simulations" element={<InternalPageLayout><SimulationsPage /></InternalPageLayout>} />
              <Route path="/simulations/create" element={<InternalPageLayout><CreateSimulation /></InternalPageLayout>} />
              <Route path="/ideas" element={<InternalPageLayout><IdeasPage /></InternalPageLayout>} />
              <Route path="/ideas/create" element={<InternalPageLayout><CreateIdea /></InternalPageLayout>} />
              <Route path="/ideas/:id/results" element={<InternalPageLayout><IdeaResultsPage /></InternalPageLayout>} />
              <Route path="/personas" element={<InternalPageLayout><ICPsPage /></InternalPageLayout>} />
              <Route path="/accounts" element={<InternalPageLayout><AccountsPage /></InternalPageLayout>} />
              <Route path="/simulations/:id/results" element={<InternalPageLayout><SimulationResultsPage /></InternalPageLayout>} />
              <Route path="/settings" element={<InternalPageLayout><SettingsPage /></InternalPageLayout>} />
              <Route path="/settings/plans" element={<InternalPageLayout><PlansPage /></InternalPageLayout>} />
            </Route>

            <Route path="*" element={<NotFoundPage />} />
          </>
        )}
      </Routes>
      
      {!isLoading && !isCheckingTerms && hasCheckedInitialTerms && showTermsModal && !showOnboarding && (
        <TermsOfServiceModal 
          open={true} 
          onAccept={acceptTerms} 
        />
      )}
      <CookieBanner />
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider theme={theme}>
        <Router future={{ 
          v7_relativeSplatPath: true,
          v7_startTransition: true 
        }}>
          <CssBaseline />
          <PrelaunchProvider>
            <AuthProvider>
              <QuotaProvider>
                <SimulationProvider>
                  <FeedbackProvider>
                    <AppContent />
                    <Analytics />
                  </FeedbackProvider>
                </SimulationProvider>
              </QuotaProvider>
            </AuthProvider>
          </PrelaunchProvider>
        </Router>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default Sentry.withSentryReactRouterV6Routing(App);
